import type { Action, GameObject, Rule, SceneContent } from "./types.ts";
// Layers are stored as one flat array. `parentId` builds the tree and the
// array order is the paint order among siblings: later layers sit on top.
export type Box = { x: number; y: number; width: number; height: number };
export const isContainer = (o: GameObject) =>
  o.kind === "group" || o.kind === "frame";
export function childrenOf(objects: GameObject[]) {
  const map = new Map<string | null, GameObject[]>();
  for (const o of objects) {
    const key = o.parentId;
    const list = map.get(key);
    if (list) list.push(o);
    else map.set(key, [o]);
  }
  return map;
}
/** Depth-first paint order: parents before their children, bottom to top. */
export function paintOrder(objects: GameObject[]) {
  const children = childrenOf(objects),
    out: { object: GameObject; depth: number }[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const o of children.get(parent) || []) {
      out.push({ object: o, depth });
      walk(o.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}
export function descendants(objects: GameObject[], ids: Iterable<string>) {
  const children = childrenOf(objects),
    out = new Set<string>();
  const walk = (id: string) => {
    for (const c of children.get(id) || [])
      if (!out.has(c.id)) {
        out.add(c.id);
        walk(c.id);
      }
  };
  for (const id of ids) walk(id);
  return out;
}
export function ancestors(objects: GameObject[], id: string) {
  const byId = new Map(objects.map((o) => [o.id, o])),
    out: GameObject[] = [];
  for (let p = byId.get(id)?.parentId; p; p = byId.get(p)?.parentId) {
    const parent = byId.get(p);
    if (!parent) break;
    out.push(parent);
  }
  return out;
}
/** Axis-aligned box of a layer after rotation. */
export function bounds(o: Box & { rotation?: number }): Box {
  const r = ((o.rotation || 0) * Math.PI) / 180;
  if (!r) return { x: o.x, y: o.y, width: o.width, height: o.height };
  const c = Math.abs(Math.cos(r)),
    s = Math.abs(Math.sin(r)),
    width = o.width * c + o.height * s,
    height = o.width * s + o.height * c;
  return {
    x: o.x + o.width / 2 - width / 2,
    y: o.y + o.height / 2 - height / 2,
    width,
    height,
  };
}
export function union(boxes: Box[]): Box | null {
  if (!boxes.length) return null;
  let x1 = Infinity,
    y1 = Infinity,
    x2 = -Infinity,
    y2 = -Infinity;
  for (const b of boxes) {
    x1 = Math.min(x1, b.x);
    y1 = Math.min(y1, b.y);
    x2 = Math.max(x2, b.x + b.width);
    y2 = Math.max(y2, b.y + b.height);
  }
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
}
export const intersects = (a: Box, b: Box) =>
  a.x < b.x + b.width &&
  a.x + a.width > b.x &&
  a.y < b.y + b.height &&
  a.y + a.height > b.y;
/** Point test that respects rotation. */
export function contains(o: GameObject, x: number, y: number) {
  const cx = o.x + o.width / 2,
    cy = o.y + o.height / 2,
    r = (-(o.rotation || 0) * Math.PI) / 180,
    dx = x - cx,
    dy = y - cy,
    lx = dx * Math.cos(r) - dy * Math.sin(r),
    ly = dx * Math.sin(r) + dy * Math.cos(r);
  return Math.abs(lx) <= o.width / 2 && Math.abs(ly) <= o.height / 2;
}
/** Group boxes follow their contents; empty groups are removed. */
export function fitGroups(objects: GameObject[]): GameObject[] {
  const children = childrenOf(objects),
    fitted = new Map<string, GameObject>(),
    empty = new Set<string>();
  const fit = (o: GameObject): GameObject => {
    if (o.kind !== "group") return fitted.get(o.id) || o;
    if (fitted.has(o.id)) return fitted.get(o.id)!;
    const kids = (children.get(o.id) || [])
      .map(fit)
      .filter((c) => !empty.has(c.id));
    const box = union(kids.map(bounds));
    if (!box) {
      empty.add(o.id);
      return o;
    }
    const next =
      box.x === o.x &&
      box.y === o.y &&
      box.width === o.width &&
      box.height === o.height
        ? o
        : { ...o, ...box, rotation: 0 };
    fitted.set(o.id, next);
    return next;
  };
  objects.forEach(fit);
  const out = objects
    .filter((o) => !empty.has(o.id))
    .map((o) => fitted.get(o.id) || o);
  return out.length === objects.length &&
    out.every((o, i) => o === objects[i])
    ? objects
    : empty.size
      ? fitGroups(out)
      : out;
}
/** Moves layers and everything inside them. */
export function translate(
  objects: GameObject[],
  ids: Iterable<string>,
  dx: number,
  dy: number,
) {
  const moving = new Set(ids);
  for (const id of descendants(objects, moving)) moving.add(id);
  return objects.map((o) =>
    moving.has(o.id) ? { ...o, x: o.x + dx, y: o.y + dy } : o,
  );
}
/** Keeps only the outermost of the selected layers. */
export function topmost(objects: GameObject[], ids: Iterable<string>) {
  const set = new Set(ids),
    inner = descendants(objects, set);
  return objects.filter((o) => set.has(o.id) && !inner.has(o.id));
}
export function group(objects: GameObject[], ids: string[], name = "Группа") {
  const picked = topmost(objects, ids).filter((o) => !o.locked);
  if (!picked.length) return null;
  // The group goes into the deepest container that holds every picked layer.
  const chains = picked.map((o) => [
      o.parentId,
      ...ancestors(objects, o.id).map((a) => a.parentId),
    ]),
    parentId =
      chains[0].find((p) => chains.every((c) => c.includes(p))) ?? null,
    box = union(picked.map(bounds))!,
    container: GameObject = {
      ...picked.at(-1)!,
      id: crypto.randomUUID(),
      name,
      kind: "group",
      parentId,
      entityId: null,
      imageId: null,
      ...box,
      rotation: 0,
      opacity: 1,
      locked: false,
      states: [],
      state: null,
      shadow: 0,
      animations: [],
      active: true,
      visible: true,
      text: "",
      player: false,
      solid: false,
      pickup: false,
      trigger: false,
      interactable: false,
      dash: false,
      speed: null,
      dashDistance: null,
      bindings: { speed: null, dashDistance: null },
    },
    moved = new Set(picked.map((o) => o.id)),
    at = objects.findIndex((o) => o.id === picked.at(-1)!.id);
  const out: GameObject[] = [];
  objects.forEach((o, i) => {
    if (i === at) out.push(container);
    out.push(moved.has(o.id) ? { ...o, parentId: container.id } : o);
  });
  return { objects: fitGroups(out), id: container.id };
}
export function ungroup(objects: GameObject[], ids: string[]) {
  const groups = new Map(
    objects.filter((o) => ids.includes(o.id) && isContainer(o)).map((o) => [
      o.id,
      o,
    ]),
  );
  if (!groups.size) return null;
  const freed: string[] = [];
  const out = objects
    .filter((o) => !groups.has(o.id))
    .map((o) => {
      const g = o.parentId ? groups.get(o.parentId) : undefined;
      if (!g) return o;
      freed.push(o.id);
      return {
        ...o,
        parentId: g.parentId,
        opacity: Math.round(o.opacity * g.opacity * 1000) / 1000,
      };
    });
  return { objects: fitGroups(out), ids: freed };
}
/**
 * Moves `ids` next to `target`: before/after it among its siblings, or as
 * the top child when `place` is "inside". Containers cannot enter themselves.
 */
export function move(
  objects: GameObject[],
  ids: string[],
  target: string | null,
  place: "before" | "after" | "inside",
) {
  const picked = topmost(objects, ids),
    blocked = descendants(
      objects,
      picked.map((o) => o.id),
    );
  if (target && (blocked.has(target) || picked.some((o) => o.id === target)))
    return objects;
  const parentId =
    place === "inside"
      ? target
      : (objects.find((o) => o.id === target)?.parentId ?? null);
  const set = new Set(picked.map((o) => o.id)),
    rest = objects.filter((o) => !set.has(o.id)),
    moved = picked.map((o) => ({ ...o, parentId }));
  let at = rest.length;
  if (target && place === "inside") {
    // Become the top child: go after the target and everything inside it.
    const inside = descendants(rest, [target]);
    rest.forEach((o, i) => {
      if (o.id === target || inside.has(o.id)) at = i + 1;
    });
  } else if (target) {
    const index = rest.findIndex((o) => o.id === target);
    at = place === "before" ? index : index + 1;
  }
  return fitGroups([...rest.slice(0, at), ...moved, ...rest.slice(at)]);
}
/** Copies layers with their contents, giving every copy a new id. */
export function duplicate(objects: GameObject[], ids: string[], offset = 24) {
  const picked = topmost(objects, ids),
    all = new Set(picked.map((o) => o.id));
  for (const id of descendants(objects, all)) all.add(id);
  const map = new Map([...all].map((id) => [id, crypto.randomUUID()])),
    copies = objects
      .filter((o) => all.has(o.id))
      .map((o) => ({
        ...structuredClone(o),
        id: map.get(o.id)!,
        parentId: (o.parentId && map.get(o.parentId)) || o.parentId,
        name: picked.some((p) => p.id === o.id)
          ? o.name.slice(0, 145) + " — копия"
          : o.name,
        x: o.x + offset,
        y: o.y + offset,
      }));
  const last = Math.max(...objects.map((o, i) => (all.has(o.id) ? i : -1)));
  return {
    objects: fitGroups([
      ...objects.slice(0, last + 1),
      ...copies,
      ...objects.slice(last + 1),
    ]),
    ids: picked.map((o) => map.get(o.id)!),
  };
}
export function remove(objects: GameObject[], ids: string[]) {
  const gone = new Set(ids);
  for (const id of descendants(objects, gone)) gone.add(id);
  return fitGroups(objects.filter((o) => !gone.has(o.id)));
}
/**
 * After a drag, layers dropped onto a frame move into it and layers dragged
 * out of their frame leave it, as in Figma. Groups keep their members.
 */
export function reparentToFrames(objects: GameObject[], ids: string[]) {
  const byId = new Map(objects.map((o) => [o.id, o])),
    frames = paintOrder(objects)
      .map((p) => p.object)
      .filter((o) => o.kind === "frame")
      .reverse();
  let out = objects;
  for (const o of topmost(objects, ids)) {
    const parent = o.parentId ? byId.get(o.parentId) : undefined;
    if (parent?.kind === "group") continue;
    const blocked = descendants(objects, [o.id]),
      cx = o.x + o.width / 2,
      cy = o.y + o.height / 2,
      frame = frames.find(
        (f) =>
          f.id !== o.id &&
          !blocked.has(f.id) &&
          cx >= f.x &&
          cx <= f.x + f.width &&
          cy >= f.y &&
          cy <= f.y + f.height,
      );
    const target = frame?.id ?? null;
    if (target === o.parentId) continue;
    if (target) out = move(out, [o.id], target, "inside");
    else {
      // Leaving every frame: land right above the old top-level frame.
      const root = ancestors(out, o.id).at(-1);
      out = move(out, [o.id], root?.id ?? null, "after");
    }
  }
  return out;
}
/**
 * Scenes saved before layers existed had no tree and painted by `layer`.
 * The server upgrades live scenes; this covers snapshots and archives.
 */
export function normalize(content: SceneContent): SceneContent {
  const fields = ["parentId", "states", "state", "shadow", "animations"],
    rules = content.rules.map(normalizeRule);
  if (content.objects.every((o) => fields.every((f) => f in o)))
    return { ...content, rules };
  const legacy = content.objects.some((o) => !("parentId" in o));
  return {
    ...content,
    rules,
    objects: content.objects
      .map((o, i) => ({ o, i }))
      .sort((a, b) => (legacy ? a.o.layer - b.o.layer : 0) || a.i - b.i)
      .map(({ o }) => ({
        ...o,
        parentId: o.parentId ?? null,
        rotation: o.rotation ?? 0,
        opacity: o.opacity ?? 1,
        locked: o.locked ?? false,
        states: o.states ?? [],
        state: o.state ?? null,
        shadow: o.shadow ?? 0,
        animations: o.animations ?? [],
      })),
  };
}
/** Fills rule fields added after a scene was saved. */
export const normalizeRule = (r: Rule): Rule => ({
  ...r,
  detail: r.detail ?? "",
  elseActions: (r.elseActions ?? []).map(normalizeAction),
  actions: r.actions.map(normalizeAction),
  conditions: r.conditions.map((c) => ({
    ...c,
    compare: c.compare ?? "value",
    other: c.other ?? "",
  })),
});
const normalizeAction = (a: Action): Action => ({
  ...a,
  targetIds: a.targetIds ?? [],
});
